'use client'

import type { PortableTextBlock } from '@portabletext/types'
import * as React from 'react'

import RichText from '@/components/elements/RichText'
import PhotosList from '@/components/PhotosList'
import type { PhotoPayload } from '@/constant/types'
import { cn } from '@/lib/utils'

type AlbumContainerProps = {
  photos: PhotoPayload[]
  title: string
  header?: string
  date?: string
  subheader?: string
  copy?: PortableTextBlock[]
  AlbumsNav?: React.ReactNode
  visibilityTester?: (node: Element | null) => void
  albumNavClassName?: string
  className?: string
}

export default function AlbumContainer({
  photos,
  title,
  header,
  date,
  subheader,
  copy,
  AlbumsNav,
  visibilityTester,
  albumNavClassName,
  className,
}: AlbumContainerProps) {
  const albumContainerRef = React.useRef<HTMLDivElement>(null)
  const albumScrollerRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!albumContainerRef.current || !albumScrollerRef.current) return
    const containerEl = albumContainerRef.current as HTMLDivElement
    const scrollForcerEl = albumScrollerRef.current as HTMLDivElement
    const onResize = () => {
      const containerHeight = containerEl.offsetHeight || 0
      scrollForcerEl.style.height = `${containerHeight + 250}px`
    }
    onResize()
    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
    }
  }, [])

  return (
    <main className='max--content-width relative'>
      {/* OTHER ALBUMS NAV */}
      {AlbumsNav && (
        <div
          id='OtherAlbums'
          className={cn(
            'sticky top-0 left-0 z-1 w-full py-20',
            albumNavClassName,
          )}
        >
          {AlbumsNav}
        </div>
      )}
      {/* ALBUM CONTENT */}
      <div
        id='AlbumContent'
        ref={albumContainerRef}
        className={cn(
          'content--padding absolute top-0 z-2 w-full bg-white',
          'border-b border-black',
          AlbumsNav && !copy && 'sI:pb-[25svh] pb-10',
          className,
        )}
      >
        <PhotosList
          photos={photos}
          date={date}
          header={header || title}
          subheader={subheader}
        />
        {copy && (
          <div className='flex h-[100svh] w-full items-center justify-center px-4 text-center'>
            <RichText content={copy} />
          </div>
        )}
        {/* Viewport tester */}
        {visibilityTester && (
          <div
            ref={visibilityTester}
            className='absolute bottom-0 left-0 h-[1px] w-full'
          />
        )}
      </div>
      {/* SCROLL FORCER */}
      {AlbumsNav && <div ref={albumScrollerRef} />}
    </main>
  )
}
